import React, { useEffect } from 'react'
import { Link, useParams } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { IconArrowLeft } from '@tabler/icons-react'

import { AdvancedTable } from '@/components/tables/AdvancedTable/AdvancedTable'
import { ErrorMessage } from '@/components/forms/ErrorMessage'
import { PageHeader } from '@/components/headers/PageHeader'
import { Pill } from '@/components/pills/Pill'

import { fetchOrgScorecards } from '@/reducers/costimizer/costimizer.actions'
import { selectOrg, selectOrgs } from '@/reducers/costimizer/costimizer.redux'

import { MigrationCell } from './components/MigrationCell'
import { scorecardOpportunity } from './Costimizer.helpers'

import './Costimizer.scss'

/**
 * One org's scorecards, the ones with the most to migrate first, each with a button to test a migration.
 *
 * A scorecard whose every criterion and eligibility prompt is already on the target model comes back with no
 * subjects, and says so rather than offering a button that would do nothing.
 *
 * ⚠ Each scorecard's results are their OWN PAGE rather than an expanded row, and that is deliberate. The
 * results are a table, and a table inside an expandable row inherits this one's cell styling — which meant
 * either changing `AdvancedTable`'s stylesheet, and with it every table in the app, or carrying page CSS to
 * undo the leak. A separate page needs neither, and gives each scorecard a link someone can send.
 */
export const CostimizerOrgPage = () => {
  const dispatch = useDispatch()
  const { organizationId } = useParams()
  const { loading, error, scorecards } = useSelector(selectOrg(organizationId))
  const orgs = useSelector(selectOrgs)

  useEffect(() => {
    dispatch(fetchOrgScorecards(organizationId))
  }, [dispatch, organizationId])

  const org = orgs.find((entry) => String(entry.organizationId) === String(organizationId))

  const rows = scorecards.map((scorecard) => ({
    ...scorecard,
    ...scorecardOpportunity(scorecard.subjects),
  }))

  const columns = [
    {
      accessor: 'name',
      label: 'Scorecard',
      isSearchable: true,
      format: (value, row) => (
        <Link to={`/costimizer/${organizationId}/${row.scorecardConfigId}`} className="table-link">
          {value}
        </Link>
      ),
    },
    {
      accessor: 'nMigratable',
      label: 'Prompts to migrate',
      format: (value, row) =>
        row.fullyMigrated ? <Pill>All migrated</Pill> : `${value} prompt${value === 1 ? '' : 's'}`,
    },
    {
      accessor: 'run',
      label: 'Migration',
      // `isSortable` is the option AdvancedTable reads, and it defaults to TRUE. This said `sortEnabled`,
      // which nothing reads — so the column was sortable and sorting it compared `run` objects.
      isSortable: false,
      format: (value, row) => (
        <MigrationCell
          scorecardConfigId={row.scorecardConfigId}
          fullyMigrated={row.fullyMigrated}
          nMigratable={row.nMigratable}
          perPromptMinutes={row.perPromptMinutes}
          serverRun={value}
        />
      ),
    },
  ]

  return (
    <>
      <PageHeader
        data-testid="costimizer-org-page"
        title={org ? org.name : `Organization ${organizationId}`}
        description={`Org ${organizationId} · Quality scorecards`}
        actions={
          <Link to="/costimizer" className="balto-button icon">
            <IconArrowLeft />
            All organizations
          </Link>
        }
      />

      {error && <ErrorMessage content="Could not load this organization's scorecards." />}

      <AdvancedTable
        testid="costimizer-scorecards-table"
        rows={rows}
        columns={columns}
        index="scorecardConfigId"
        loading={loading}
        defaultOrderBy="nMigratable"
        defaultOrder="desc"
        noDataHeader="No Quality scorecards"
        noDataText="This organization has no active scorecards with Quality prompts."
      />
    </>
  )
}
